import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import type { LatLon } from '@windy/interfaces';

import { pluginConfig } from '../config';
import { saveSetting, Settings } from '../util/settings';
import { DEFAULT_MODEL, getAvailableModels, getSupportedModelName, METEOBLUE_AI_MODEL } from '../util/utils';
import * as forecastSlice from './forecast-slice';
import * as pluginSlice from './plugin-slice';
import type { RootState } from './store';

const { map: windyMap, markers } = W.map;
const windyRootScope = W.rootScope;

// Pending in-flight location name request that can be aborted when location changes.
let inFlightLocationPromise: { abort: () => void } | undefined;

/**
 * Dispatches fetchLocationName to retrieve reverse-geocoded name, aborting any prior pending request.
 */
export const updateLocationName =
  (location: LatLon): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch) => {
    inFlightLocationPromise?.abort();
    inFlightLocationPromise = dispatch(pluginSlice.fetchLocationName(location));
  };

// Cross slice thunks

export type TimeStep = {
  direction: 'forward' | 'backward';
  size: 'hour' | 'day';
};

export const updateTime =
  (step: TimeStep): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const stepDays = step.size === 'day';

    const windyStore = W.store;
    let timeMs = windyStore.get('timestamp');
    const stepHour = 3600 * 1000 * (step.direction === 'forward' ? 1 : -1);

    if (stepDays) {
      const state = getState();
      const modelName = pluginSlice.selModelName(state);
      const location = pluginSlice.selLocation(state);
      const isWindyDataAvailable = forecastSlice.selIsWindyDataAvailable(state, modelName, location);

      if (isWindyDataAvailable) {
        // Step to the next/previous at 13h when we know the local TZ offset.
        const date = new Date(timeMs);
        date.setUTCMinutes(0);
        timeMs = date.getTime();
        const utcOffset = forecastSlice.selTzOffsetH(state, modelName, location);
        const currentHour = (date.getUTCHours() + utcOffset) % 24;
        const deltaHours = (13 - currentHour) * (step.direction === 'forward' ? 1 : -1);
        timeMs += stepHour * (deltaHours > 0 ? deltaHours : 24 + deltaHours);
      } else {
        // Step +/- 24 when TZ offset is not known
        timeMs += stepHour * 24;
      }
    } else {
      timeMs += stepHour;
    }

    windyStore.set('timestamp', timeMs);
  };

/**
 * Thunk to set the location.
 *
 * - Update the location
 * - Move the marker
 * - Fetch the forecast
 */
export const changeLocation =
  (location: LatLon): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    let modelName = pluginSlice.selModelName(getState());
    const availableModels = getAvailableModels(location);
    if (availableModels.length > 0 && !availableModels.includes(modelName)) {
      modelName = availableModels.includes(DEFAULT_MODEL) ? DEFAULT_MODEL : availableModels[0];
      dispatch(pluginSlice.setModelName(modelName));
      saveSetting(Settings.model, modelName);
    }
    const tileProduct = modelName === METEOBLUE_AI_MODEL ? DEFAULT_MODEL : modelName;
    if (W.store.get('product') !== tileProduct) {
      W.store.set('product', tileProduct);
    }
    dispatch(forecastSlice.fetchForecast({ modelName, location }));
    dispatch(pluginSlice.setLocation(location));
    saveSetting(Settings.location, JSON.stringify(location));

    maybeCreateAndMoveMarker(location);

    updateUrl(getState());
  };

export const changeModel =
  (modelName: string): ThunkAction<void, RootState, unknown, UnknownAction> =>
  (dispatch, getState) => {
    const currentModel = pluginSlice.selModelName(getState());
    const supportedModel = getSupportedModelName(modelName);
    if (currentModel === supportedModel) {
      return;
    }
    saveSetting(Settings.model, supportedModel);
    const tileProduct = supportedModel === METEOBLUE_AI_MODEL ? DEFAULT_MODEL : supportedModel;
    if (W.store.get('product') !== tileProduct) {
      W.store.set('product', tileProduct);
    }
    const location = pluginSlice.selLocation(getState());
    dispatch(forecastSlice.fetchForecast({ modelName: supportedModel, location }));
    dispatch(pluginSlice.setModelName(supportedModel));
    updateUrl(getState());
  };

function updateUrl(state: RootState) {
  const location = pluginSlice.selLocation(state);
  const modelName = pluginSlice.selModelName(state);
  W.location.setUrl(pluginConfig.name, { modelName, ...location } as any);
}

let marker: L.Marker | undefined;

export function maybeCreateAndMoveMarker(location: LatLon) {
  if (location.lat === 0 && location.lon === 0) {
    return;
  }
  const { lon: lng, lat } = location;
  if (marker) {
    marker.setLatLng({ lat, lng });
    return;
  }

  marker = L.marker(
    { lat, lng },
    {
      icon: markers.pulsatingIcon,
      zIndexOffset: -300,
    },
  ).addTo(windyMap);
}

export function maybeRemoveMarker() {
  if (marker) {
    windyMap.removeLayer(marker);
    marker = undefined;
  }
}

/**
 * Centers the map view at a specified location, adjusting for mobile/tablet view if necessary.
 * @param location - The latitude and longitude coordinates to center the map at.
 */
export function centerMap(location: LatLon) {
  if (windyRootScope.isMobileOrTablet) {
    const pluginContent = document.querySelector(`#plugin-${pluginConfig.name}`) as HTMLDivElement;
    if (!pluginContent) {
      return;
    }
    // The center should be on the portion of the map visible above the plugin.
    const pluginHeight = pluginContent.offsetHeight;
    const mapHeight = windyMap.getSize().y;
    const bounds = windyMap.getBounds();
    const deltaLat = bounds.getSouth() - bounds.getNorth();
    const centerLat = location.lat + ((deltaLat / mapHeight) * pluginHeight) / 2;
    windyMap.panTo({ lng: location.lon, lat: centerLat });
  } else {
    windyMap.panTo({ lng: location.lon, lat: location.lat });
  }
}

// Subscription

type Subscription = () => void;

const subscriptions: Subscription[] = [];

export function addSubscription(fn: Subscription) {
  subscriptions.push(fn);
}

export function cancelAllSubscriptions() {
  if (inFlightLocationPromise) {
    inFlightLocationPromise.abort();
    inFlightLocationPromise = undefined;
  }
  for (const fn of subscriptions) {
    fn();
  }
  subscriptions.length = 0;
}
